import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Button, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import InputSelect from '../../ui/Select';
import { INPUT_DEVICES, publishStream } from './utils';
import { TOKEN_ERROR } from '../../modules/broadcast/reducer';

const BroadcastVideo = ({ streamResolutions, videoDevices, audioDevices, updateDevices }) => {
  const videoRef = useRef();
  const dispatch = useDispatch();
  const { eventId } = useParams();

  const [audioOn, setAudioOn] = useState(true);
  const [videoOn, setVideoOn] = useState(true);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamPublisher, setStreamPublisher] = useState(new MediaStream());
  const [selectedAudioDevice, setSelectedAudioDevice] = useState('');
  const [selectedVideoDevice, setSelectedVideoDevice] = useState('');

  const { token, error } = useSelector(state => state.broadcast);

  const changeMediaSelection = (value, isVideo, replaceTrack = false) => {
    const params = {
      ...streamResolutions,
      token,
      eventId,
      replaceTrack,
      audio: selectedAudioDevice,
      video: selectedVideoDevice
    };

    if (isVideo) {
      setSelectedVideoDevice(value);
      params.video = value;
    } else {
      setSelectedAudioDevice(value);
      params.audio = value;
    }

    if (!error && token) {
      publishStream(params, ({ mediaStream, publisher, error }) => {
        if (error) {
          dispatch({
            type: TOKEN_ERROR,
            payload: error
          });
        } else if (videoRef && videoRef.current) {
          if (params.audio === INPUT_DEVICES.MICROPHONE && params.video === INPUT_DEVICES.CAMERA) {
            updateDevices();
          }
          setIsStreaming(true);
          if (replaceTrack) {
            const tracks = isVideo ? mediaStream.getVideoTracks() : mediaStream.getAudioTracks();
            const [stream = {}] = tracks || [];
            streamPublisher.options.mediaStream.addTrack(stream);
            if (stream) streamPublisher.webRTCPeer.replaceTrack(stream);
          } else setStreamPublisher(publisher);
          videoRef.current.srcObject = mediaStream;
        }
      });
    }
  };

  // Turn on / off the audio
  const enableAudioHandler = enabled => {
    setAudioOn(enabled);
    if (isStreaming) {
      const [data = {}] = streamPublisher.options.mediaStream.getAudioTracks() || [];
      data.enabled = enabled;
    }
  };

  // Turn on / off the video
  const enableVideoHandler = enabled => {
    setVideoOn(enabled);
    if (isStreaming) {
      if (!enabled) {
        streamPublisher.options.mediaStream.getVideoTracks().forEach(data => {
          data.stop();
          streamPublisher.options.mediaStream.removeTrack(data);
        });
      } else {
        changeMediaSelection(selectedVideoDevice, true, true);
      }
    }
  };

  // Turn on / off audio and video
  const pausePlayStream = () => {
    const isVideoStreaming = videoOn || audioOn;
    enableAudioHandler(!isVideoStreaming);
    enableVideoHandler(!isVideoStreaming);
  };

  return (
    <Grid>
      <Box className="broadcast-wrapper" sx={{ mb: 3 }}>
        <video
          id="millicast-publisher"
          autoPlay
          muted="muted"
          ref={videoRef}
          height={240}
          width={426}
        />
        <Box className="settings_group float-left" visibility={isStreaming ? 'hidden' : ''}>
          {error ? (
            <Box>
              <Typography variant="body2" color="#600B0D" textTransform="capitalize">
                <FontAwesomeIcon className="error-icon" icon="exclamation-triangle" />
                {error}
              </Typography>
            </Box>
          ) : (
            <>
              <InputSelect
                labelClassName="broadcast-select-label"
                className="broadcast-select"
                sx={{ width: 230 }}
                placeholder="Please select an option"
                label="Camera"
                onChange={value => changeMediaSelection(value, true)}
                size="small"
                menus={videoDevices}
              />
              <InputSelect
                labelClassName="broadcast-select-label"
                className="broadcast-select"
                sx={{ width: 230 }}
                placeholder="Please select an option"
                label="Microphone"
                onChange={value => changeMediaSelection(value, false)}
                size="small"
                menus={audioDevices}
              />
            </>
          )}
        </Box>
        <Box className="float-left camera-message">
          Please note: If you are using Google Chrome and you wish to select a camera that is different than
          Chrome&apos;s browser default camera you&apos;ll need to update your settings in Chrome directly.
          Instructions can be found <a href="https://support.google.com/chrome/answer/2693767?hl=en&co=GENIE.Platform%3DDesktop" rel="noopener noreferrer" target="_blank">here</a>.
        </Box>
        <Box className="clearfix" />
        <Box className="control_bar" width={426}>
          <span
            className="controls"
            onClick={() => {
              const enabled = !audioOn;
              setAudioOn(enabled);
              const [data = {}] = videoRef?.current?.srcObject?.getAudioTracks() || [];
              data.enabled = enabled;
            }}
          >
            <span className={`broadcast_image audio_${audioOn ? 'on' : 'off'}`} />
            <span className="control_text">Audio {audioOn ? 'On' : 'Off'}</span>
          </span>
          <span
            className="controls"
            onClick={() => {
              const enabled = !videoOn;
              setVideoOn(enabled);
              const [data = {}] = videoRef?.current?.srcObject?.getVideoTracks() || [];
              data.enabled = enabled;
            }}
          >
            <span className={`broadcast_image video_${videoOn ? 'on' : 'off'}`} />
            <span className="ml-1 control_text">Video {videoOn ? 'On' : 'Off'}</span>
          </span>
          {isStreaming && (
          <Button
            className="pauseBtn"
            variant="contained"
            color="success"
            size="small"
            onClick={audioDevices}
          />
          )}
        </Box>
        <Box className="control_bar" width={426}>
          <span className="controls" onClick={() => enableAudioHandler(!audioOn)}>
            <span className={`broadcast_image audio_${audioOn ? 'on' : 'off'}`} />
            <span className="control_text">Audio {audioOn ? 'On' : 'Off'}</span>
          </span>
          <span className="controls" onClick={() => enableVideoHandler(!videoOn)}>
            <span className={`broadcast_image video_${videoOn ? 'on' : 'off'}`} />
            <span className="ml-1 control_text">Video {videoOn ? 'On' : 'Off'}</span>
          </span>
          {isStreaming && (
            <Button
              className="pauseBtn"
              variant="contained"
              color="success"
              size="small"
              onClick={pausePlayStream}
            >
              {videoOn || audioOn ? 'Pause Stream' : 'Resume Stream'}
            </Button>
          )}
        </Box>
      </Box>
    </Grid>
  );
};

export default BroadcastVideo;
